/**
 * Menu Component - Template
 * ==========================
 *
 * Templates HTML del componente Menu.
 */

/**
 * Template principal del Menu
 */
export const MENU_TEMPLATE: string = `
<nav
  class="ec-menu"
  ng-class="$ctrl.getContainerClasses()"
  role="navigation"
>
  <!-- ======================================================================
       MODO DATA-DRIVEN (binding items) — comportamiento histórico intacto.
       ng-repeat + ng-if en el mismo elemento: patrón ya usado en
       MENU_NAVBAR_TEMPLATE (ngRepeat prioridad 1000 procesa primero, ngIf
       prioridad 600 filtra cada instancia repetida).
       ====================================================================== -->
  <div
    ng-repeat="item in $ctrl.parsedItems track by item.id"
    ng-if="!$ctrl.isCompound"
    class="ec-menu-item-wrapper"
  >
    <!-- Divider -->
    <div
      ng-if="item.divider"
      ng-class="$ctrl.getDividerClasses()"
    ></div>

    <!-- Menu Item -->
    <div ng-if="!item.divider">
      <a
        ng-class="$ctrl.getItemClasses(item)"
        ng-click="$ctrl.handleItemClick($event, item)"
        ng-mouseenter="$ctrl.handleItemHover($event, item)"
        ng-href="{{ item.href || '' }}"
        role="menuitem"
        ng-attr-aria-disabled="{{ item.disabled }}"
        ng-attr-aria-expanded="{{ $ctrl.hasChildren(item) ? $ctrl.isExpanded(item) : undefined }}"
      >
        <!-- Icon -->
        <span
          ng-if="$ctrl.showIcons && item.icon && item.icon !== 'none'"
          ng-class="$ctrl.getIconClasses()"
          ng-bind-html="$ctrl.getIcon(item.id)"
        ></span>

        <!-- Label -->
        <span ng-class="$ctrl.getLabelClasses()">
          {{ item.label }}
        </span>

        <!-- Badge -->
        <span
          ng-if="item.badge"
          ng-class="$ctrl.getBadgeClasses(item)"
        >
          {{ item.badge }}
        </span>

        <!-- Chevron for submenu -->
        <span
          ng-if="$ctrl.hasChildren(item)"
          ng-class="$ctrl.getChevronClasses(item)"
          ng-bind-html="$ctrl.getIcon('chevron-right')"
        ></span>
      </a>

      <!-- Submenu -->
      <div
        ng-if="$ctrl.hasChildren(item) && $ctrl.isExpanded(item)"
        ng-class="$ctrl.getSubmenuClasses()"
        role="menu"
      >
        <div
          ng-repeat="child in item.children track by child.id"
          class="ec-menu-subitem"
        >
          <a
            ng-class="$ctrl.getItemClasses(child)"
            ng-click="$ctrl.handleItemClick($event, child)"
            ng-href="{{ child.href || '' }}"
            role="menuitem"
            ng-attr-aria-disabled="{{ child.disabled }}"
          >
            <span
              ng-if="$ctrl.showIcons && child.icon && child.icon !== 'none'"
              ng-class="$ctrl.getIconClasses()"
              ng-bind-html="$ctrl.getIcon(child.id)"
            ></span>
            <span ng-class="$ctrl.getLabelClasses()">
              {{ child.label }}
            </span>
            <span
              ng-if="child.badge"
              ng-class="$ctrl.getBadgeClasses(child)"
            >
              {{ child.badge }}
            </span>
          </a>
        </div>
      </div>
    </div>
  </div>

  <!-- ======================================================================
       MODO COMPOUND (composición vía ecMenuItem)
       NO gatear con ng-if="$ctrl.isCompound": isCompound solo se vuelve true
       DESPUÉS de que los ec-menu-item se registren, lo que requiere que este
       ng-transclude ya haya linkeado — gatearlo así crea un deadlock (nunca
       linkea, nunca registra, nunca es compound).
       Sí se gatea con "!$ctrl.items || !$ctrl.items.length": a diferencia de
       isCompound, esto se conoce sincrónicamente desde los bindings (sin
       depender del registro de hijos), así que no hay deadlock. Evita
       double-render cuando un consumidor pasa items Y ec-menu-item
       transcluidos a la vez.
       Sin hijos transcluidos queda vacío (invisible, cero impacto visual).
       ec-contents (display:contents) hace transparente este ul para el
       layout flex de nav: cada ec-menu-item transcluido queda como hijo
       flex directo de nav, igual que los div del modo data-driven (paridad
       de orientación horizontal/vertical + gap).
       ====================================================================== -->
  <ul
    class="ec-contents"
    ng-if="!$ctrl.items || !$ctrl.items.length"
    ng-transclude
  ></ul>
</nav>
`;

/**
 * Template compacto para dropdowns
 */
export const MENU_DROPDOWN_TEMPLATE: string = `
<div class="ec-menu-dropdown ec-relative">
  <!-- Trigger -->
  <button
    type="button"
    class="ec-flex ec-items-center ec-gap-2 ec-px-4 ec-py-2 ec-rounded-lg ec-bg-white ec-border ec-border-gray-200 hover:ec-bg-gray-50 ec-transition-colors"
    ng-click="$ctrl.isOpen = !$ctrl.isOpen"
  >
    <span>{{ $ctrl.triggerText || 'Menu' }}</span>
    <span
      class="ec-w-4 ec-h-4 ec-transition-transform"
      ng-class="{'ec-rotate-180': $ctrl.isOpen}"
      ng-bind-html="$ctrl.getIcon('chevron-down')"
    ></span>
  </button>

  <!-- Dropdown -->
  <nav
    ng-if="$ctrl.isOpen"
    ng-class="$ctrl.getContainerClasses()"
    class="ec-absolute ec-top-full ec-left-0 ec-mt-2 ec-min-w-48 ec-z-50"
    role="menu"
  >
    <div
      ng-repeat="item in $ctrl.parsedItems track by item.id"
    >
      <div
        ng-if="item.divider"
        ng-class="$ctrl.getDividerClasses()"
      ></div>
      <a
        ng-if="!item.divider"
        ng-class="$ctrl.getItemClasses(item)"
        ng-click="$ctrl.handleItemClick($event, item); $ctrl.isOpen = false"
        ng-href="{{ item.href || '' }}"
        role="menuitem"
      >
        <span
          ng-if="$ctrl.showIcons && item.icon"
          ng-class="$ctrl.getIconClasses()"
          ng-bind-html="$ctrl.getIcon(item.id)"
        ></span>
        <span ng-class="$ctrl.getLabelClasses()">{{ item.label }}</span>
        <span
          ng-if="item.badge"
          ng-class="$ctrl.getBadgeClasses(item)"
        >{{ item.badge }}</span>
      </a>
    </div>
  </nav>
</div>
`;

/**
 * Template de navegación horizontal (navbar)
 */
export const MENU_NAVBAR_TEMPLATE: string = `
<nav
  class="ec-menu-navbar ec-flex ec-items-center ec-justify-between ec-px-6 ec-py-3"
  ng-class="$ctrl.getVariantClasses().container"
  role="navigation"
>
  <!-- Logo/Brand slot -->
  <div class="ec-flex-shrink-0" ng-transclude="brand"></div>

  <!-- Menu items -->
  <div class="ec-flex ec-items-center" ng-class="$ctrl.getSizeClasses().gap">
    <a
      ng-repeat="item in $ctrl.parsedItems track by item.id"
      ng-if="!item.divider"
      ng-class="$ctrl.getItemClasses(item)"
      ng-click="$ctrl.handleItemClick($event, item)"
      ng-href="{{ item.href || '' }}"
      role="menuitem"
    >
      <span
        ng-if="$ctrl.showIcons && item.icon"
        ng-class="$ctrl.getIconClasses()"
        ng-bind-html="$ctrl.getIcon(item.id)"
      ></span>
      <span ng-class="$ctrl.getLabelClasses()">{{ item.label }}</span>
      <span
        ng-if="item.badge"
        ng-class="$ctrl.getBadgeClasses(item)"
      >{{ item.badge }}</span>
    </a>
  </div>

  <!-- Actions slot -->
  <div class="ec-flex ec-items-center ec-gap-3" ng-transclude="actions"></div>
</nav>
`;

/**
 * Template del hijo compound ecMenuItem.
 * Clases delegadas a `$ctrl.getClasses()/getIconClasses()/getLabelClasses()/
 * getBadgeClasses()`, que a su vez reusan (vía el padre `ecMenu`) las MISMAS
 * clases que el item data-driven — paridad pixel entre ambos modos.
 */
export const MENU_ITEM_TEMPLATE: string = `
<li
  ng-class="$ctrl.getClasses()"
  ng-click="$ctrl.handleClick($event)"
  ng-keydown="$ctrl.handleKeydown($event)"
  role="menuitem"
  tabindex="{{ $ctrl.disabled ? -1 : 0 }}"
  ng-attr-aria-disabled="{{ $ctrl.disabled }}"
>
  <!-- Icon (paridad con el item data-driven: honra showIcons del padre) -->
  <span
    ng-if="$ctrl.menu && $ctrl.menu.showIcons !== false && $ctrl.icon"
    ng-class="$ctrl.getIconClasses()"
    ng-bind-html="$ctrl.trustedIcon"
  ></span>

  <!-- Label (contenido transcluido) -->
  <span ng-class="$ctrl.getLabelClasses()" ng-transclude></span>

  <!-- Badge -->
  <span
    ng-if="$ctrl.badge"
    ng-class="$ctrl.getBadgeClasses()"
  >
    {{ $ctrl.badge }}
  </span>
</li>
`;

export default MENU_TEMPLATE;
